import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClinet from "../../api/Api.js";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit"; // <-- import edit icon

// Function to fetch PayType data
const fetchPayTypes = async () => {
  const response = await apiClinet.get(
    `${process.env.REACT_APP_NEW_DATA}/getPayType`
  );
  return response.data.paytypes;
};

// Function to add a new PayType
const addPayType = async (newPayType) => {
  await apiClinet.post(`${process.env.REACT_APP_NEW_DATA}/addpaytype`, {
    name: newPayType,
  });
};

// Function to delete a PayType
const deletePayType = async (paytype_id) => {
  await apiClinet.delete(
    `${process.env.REACT_APP_NEW_DATA}/deletepaytype/${paytype_id}`
  );
};

// Function to update a PayType
const updatePayType = async ({ id, name }) => {
  const paytype_id = id;
  await apiClinet.put(
    `${process.env.REACT_APP_NEW_DATA}/updatepaytype/${paytype_id}`,
    {
      name,
    }
  );
};

export default function PayType() {
  const [newPayType, setNewPayType] = useState("");
  const [open, setOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editPayTypeId, setEditPayTypeId] = useState(null);
  const [editPayTypeName, setEditPayTypeName] = useState("");

  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Fetch PayTypes
  const { data, error, isLoading } = useQuery({
    queryKey: ["payTypes"],
    queryFn: fetchPayTypes,
  });

  // Mutation to add
  const mutation = useMutation({
    mutationFn: addPayType,
    onSuccess: () => {
      queryClient.invalidateQueries(["payTypes"]);
      setNewPayType("");
      handleClose();
    },
    onError: (error) => {
      console.error("Error adding PayType:", error);
    },
  });

  // Mutation to delete
  const deleteMutation = useMutation({
    mutationFn: deletePayType,
    onSuccess: () => {
      queryClient.invalidateQueries(["payTypes"]);
    },
    onError: (error) => {
      console.error("Error deleting PayType:", error);
    },
  });

  // Mutation to edit/update
  const editMutation = useMutation({
    mutationFn: updatePayType,
    onSuccess: () => {
      queryClient.invalidateQueries(["payTypes"]);
      handleEditDialogClose();
    },
    onError: (error) => {
      console.error("Error updating PayType:", error);
    },
  });

  // Add handlers
  const handleAddPayType = () => {
    if (newPayType.trim()) {
      mutation.mutate(newPayType);
    }
  };

  const handleDeletePayType = (payTypeId) => () => {
    if (window.confirm("Are you sure you want to delete this PayType?")) {
      deleteMutation.mutate(payTypeId);
    }
  };

  // Edit handlers
  const handleEditDialogOpen = (payType) => {
    setEditPayTypeId(payType.paytype_id);
    setEditPayTypeName(payType.name);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditPayTypeId(null);
    setEditPayTypeName("");
  };

  const handleUpdatePayType = () => {
    if (editPayTypeName.trim()) {
      editMutation.mutate({ id: editPayTypeId, name: editPayTypeName });
    }
  };

  // Dialog open/close
  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;

  return (
    <>
      <Box sx={{ margin: isMobile ? 2 : 5 }}>
        {/* Page Title */}
        <Typography
          variant={isMobile ? "h5" : "h4"}
          gutterBottom
          sx={{ textAlign: isMobile ? "center" : "left" }}
        >
          Pay Type
        </Typography>

        {/* Add PayType Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-end",
            marginBottom: 2,
          }}
        >
          <Button variant="contained" color="primary" onClick={handleClickOpen}>
            Add PayType
          </Button>
        </Box>

        {/* PayTypes Table */}
        <TableContainer
          component={Paper}
          sx={{
            marginX: isMobile ? 1 : 5,
          }}
        >
          <Table size={isMobile ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                <TableCell>PayType ID</TableCell>
                <TableCell>PayType Name</TableCell>
                <TableCell align="center">Edit</TableCell>
                <TableCell align="center">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.length > 0 ? (
                data.map((payType) => (
                  <TableRow key={payType.paytype_id}>
                    <TableCell>{payType.paytype_id}</TableCell>
                    <TableCell>{payType.name}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={() => handleEditDialogOpen(payType)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        onClick={handleDeletePayType(payType.paytype_id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Add Dialog */}
      <Dialog fullScreen={isMobile} open={open} onClose={handleClose}>
        <DialogTitle>Add PayType</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New PayType Name"
            type="text"
            fullWidth
            value={newPayType}
            onChange={(e) => setNewPayType(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddPayType} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        fullScreen={isMobile}
        open={editDialogOpen}
        onClose={handleEditDialogClose}
      >
        <DialogTitle>Edit PayType</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="PayType Name"
            type="text"
            fullWidth
            value={editPayTypeName}
            onChange={(e) => setEditPayTypeName(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpdatePayType} color="primary">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
